import { useEffect, useState } from "react";
import { CheckCircle2, Lock, ExternalLink, Save } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import { getCurrentOrg } from "@/api/organizations";
import {
  getAuthorizationUrl,
  getSSOConfig,
  upsertSSOConfig,
} from "@/api/sso";
import {
  SSO_PROVIDER_LABEL,
  type SSOConfigurationRead,
  type SSOConfigurationUpsert,
  type SSOProvider,
} from "@/types/sso";

const PROVIDERS: SSOProvider[] = ["GOOGLE", "MICROSOFT", "SAML"];

type FormState = SSOConfigurationUpsert & { loaded: boolean };

const BLANK: FormState = {
  loaded: false,
  is_enabled: false,
  client_id: "",
  client_secret: "",
  tenant: "",
  allowed_email_domains: "",
  idp_entity_id: "",
  idp_sso_url: "",
  idp_certificate: "",
  sp_entity_id: "",
  default_role: "EMPLOYEE",
  jit_provisioning: true,
};

/**
 * SSO / SAML configuration (SUPER_ADMIN only).
 *
 * One card per supported provider. On mount we fetch the saved config for
 * each; 404 means "not configured yet" and we start from a blank form.
 */
export function SSOSettings() {
  const { addToast } = useToast();
  const [orgSlug, setOrgSlug] = useState<string>("");
  const [forms, setForms] = useState<Record<SSOProvider, FormState>>({
    GOOGLE: { ...BLANK },
    MICROSOFT: { ...BLANK },
    SAML: { ...BLANK },
  });
  const [saving, setSaving] = useState<SSOProvider | null>(null);
  const [testing, setTesting] = useState<SSOProvider | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const org = await getCurrentOrg();
        setOrgSlug(org.slug);
      } catch {
        // non-fatal
      }
      const entries = await Promise.all(
        PROVIDERS.map(async (p) => {
          try {
            const cfg = await getSSOConfig(p);
            return [p, fromConfig(cfg)] as const;
          } catch {
            return [p, { ...BLANK, loaded: true }] as const;
          }
        }),
      );
      setForms(
        Object.fromEntries(entries) as Record<SSOProvider, FormState>,
      );
    })();
  }, []);

  const update = (p: SSOProvider, patch: Partial<FormState>) =>
    setForms((prev) => ({ ...prev, [p]: { ...prev[p], ...patch } }));

  const save = async (p: SSOProvider) => {
    setSaving(p);
    try {
      const cfg = await upsertSSOConfig(p, toPayload(forms[p]));
      update(p, fromConfig(cfg));
      addToast("success", `${SSO_PROVIDER_LABEL[p]} configuration saved`);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to save configuration";
      addToast("error", msg);
    } finally {
      setSaving(null);
    }
  };

  const test = async (p: SSOProvider) => {
    if (!orgSlug) {
      addToast("error", "Organization slug not loaded yet");
      return;
    }
    setTesting(p);
    try {
      const { authorization_url } = await getAuthorizationUrl(
        p,
        orgSlug,
        window.location.pathname,
      );
      window.open(authorization_url, "_blank", "noopener,noreferrer");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Test sign-in failed";
      addToast("error", msg);
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Single Sign-On
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted">
          Configure SSO for your organization. Users with a matching identity
          at the provider will be able to sign in with one click from the
          login page. SAML supports any IdP (Okta, Azure AD, OneLogin,
          Google Workspace SAML).
        </p>
      </Card>

      {PROVIDERS.map((p) => {
        const f = forms[p];
        const isSaml = p === "SAML";
        return (
          <Card key={p}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {SSO_PROVIDER_LABEL[p]}
                {f.is_enabled && (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Enabled
                  </Badge>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => test(p)}
                  isLoading={testing === p}
                  disabled={!f.is_enabled}
                  title={
                    f.is_enabled ? "Open provider login flow in new tab" : "Enable first"
                  }
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Test SSO
                </Button>
                <Button
                  size="sm"
                  onClick={() => save(p)}
                  isLoading={saving === p}
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </Button>
              </div>
            </CardHeader>

            <div className="space-y-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!f.is_enabled}
                  onChange={(e) => update(p, { is_enabled: e.target.checked })}
                />
                Enable {SSO_PROVIDER_LABEL[p]} sign-in for this organization
              </label>

              {!isSaml ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    label="Client ID"
                    value={f.client_id ?? ""}
                    onChange={(e) => update(p, { client_id: e.target.value })}
                  />
                  <Input
                    label="Client secret"
                    type="password"
                    placeholder={f.loaded ? "(unchanged)" : ""}
                    value={f.client_secret ?? ""}
                    onChange={(e) =>
                      update(p, { client_secret: e.target.value })
                    }
                  />
                  {p === "MICROSOFT" && (
                    <Input
                      label="Azure AD tenant"
                      placeholder="common or tenant-id"
                      value={f.tenant ?? ""}
                      onChange={(e) => update(p, { tenant: e.target.value })}
                    />
                  )}
                  <Input
                    label="Allowed email domains (comma-separated)"
                    placeholder="acme.com, acme.co.uk"
                    value={f.allowed_email_domains ?? ""}
                    onChange={(e) =>
                      update(p, { allowed_email_domains: e.target.value })
                    }
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    label="IdP entity ID (issuer)"
                    value={f.idp_entity_id ?? ""}
                    onChange={(e) =>
                      update(p, { idp_entity_id: e.target.value })
                    }
                  />
                  <Input
                    label="IdP SSO URL"
                    placeholder="https://idp.example.com/sso/..."
                    value={f.idp_sso_url ?? ""}
                    onChange={(e) => update(p, { idp_sso_url: e.target.value })}
                  />
                  <Input
                    label="SP entity ID (leave blank for default)"
                    value={f.sp_entity_id ?? ""}
                    onChange={(e) =>
                      update(p, { sp_entity_id: e.target.value })
                    }
                  />
                  <Input
                    label="Allowed email domains"
                    value={f.allowed_email_domains ?? ""}
                    onChange={(e) =>
                      update(p, { allowed_email_domains: e.target.value })
                    }
                  />
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium">
                      IdP certificate (PEM)
                    </label>
                    <textarea
                      className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
                      rows={6}
                      value={f.idp_certificate ?? ""}
                      onChange={(e) =>
                        update(p, { idp_certificate: e.target.value })
                      }
                      placeholder="-----BEGIN CERTIFICATE-----..."
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium">
                    Default role for new users
                  </label>
                  <select
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={f.default_role ?? "EMPLOYEE"}
                    onChange={(e) =>
                      update(p, { default_role: e.target.value })
                    }
                  >
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                    <option value="HR_ADMIN">HR Admin</option>
                  </select>
                </div>
                <label className="mt-5 inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!f.jit_provisioning}
                    onChange={(e) =>
                      update(p, { jit_provisioning: e.target.checked })
                    }
                  />
                  Auto-provision users on first login
                </label>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function fromConfig(cfg: SSOConfigurationRead): FormState {
  return {
    loaded: true,
    is_enabled: cfg.is_enabled,
    client_id: cfg.client_id ?? "",
    client_secret: "",
    tenant: cfg.tenant ?? "",
    allowed_email_domains: cfg.allowed_email_domains ?? "",
    idp_entity_id: cfg.idp_entity_id ?? "",
    idp_sso_url: cfg.idp_sso_url ?? "",
    idp_certificate: cfg.idp_certificate ?? "",
    sp_entity_id: cfg.sp_entity_id ?? "",
    default_role: cfg.default_role ?? "EMPLOYEE",
    jit_provisioning: cfg.jit_provisioning ?? true,
  };
}

function toPayload(f: FormState): SSOConfigurationUpsert {
  const out: SSOConfigurationUpsert = {
    is_enabled: f.is_enabled,
    client_id: nullify(f.client_id),
    tenant: nullify(f.tenant),
    allowed_email_domains: nullify(f.allowed_email_domains),
    idp_entity_id: nullify(f.idp_entity_id),
    idp_sso_url: nullify(f.idp_sso_url),
    idp_certificate: nullify(f.idp_certificate),
    sp_entity_id: nullify(f.sp_entity_id),
    default_role: f.default_role ?? "EMPLOYEE",
    jit_provisioning: f.jit_provisioning ?? true,
  };
  // Only send client_secret if the admin typed one — otherwise server keeps
  // the existing value (we never send the stored secret back to the client).
  if (f.client_secret) out.client_secret = f.client_secret;
  return out;
}

function nullify(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t.length === 0 ? null : t;
}
